'use client';

import { useTranslations } from 'next-intl';
import styles from './ValueComparison.module.css';

/**
 * Traditional UX research vs Interface Audit, as a table of differences.
 *
 * This used to be a price-bar chart ($50,000+ vs $99) with a "VS" divider.
 * A $99 automated report and a multi-week research engagement are different
 * products, so the comparison now names what differs (method, time, inputs,
 * context, cost, output) and says plainly that the audit is a starting point,
 * not a replacement. Rows follow the site's numbered-timeline pattern.
 */
const ROWS = ['method', 'time', 'inputs', 'context', 'cost', 'output'] as const;

export function ValueComparison() {
  const t = useTranslations('audit.comparison');

  return (
    <section className={styles.section}>
      <div className="audit-container">
        <div className={styles.header}>
          <span className={styles.label}>{t('label')}</span>
          <h2 className={styles.title}>{t('title')}</h2>
        </div>

        <div className={styles.table} role="table" aria-label={t('title')}>
          <div className={`${styles.row} ${styles.headRow}`} role="row">
            <span className={styles.rowLabel} role="columnheader" aria-hidden="true" />
            <span className={styles.colHead} role="columnheader">
              {t('columns.traditional')}
            </span>
            <span className={`${styles.colHead} ${styles.colHeadUs}`} role="columnheader">
              {t('columns.us')}
            </span>
          </div>

          {ROWS.map((row, index) => (
            <div key={row} className={styles.row} role="row">
              <span className={styles.rowLabel} role="rowheader">
                <span className={styles.rowIndex}>{String(index + 1).padStart(2, '0')}</span>
                {t(`rows.${row}.label`)}
              </span>
              <span className={styles.cell} role="cell">
                {t(`rows.${row}.traditional`)}
              </span>
              <span className={`${styles.cell} ${styles.cellUs}`} role="cell">
                {t(`rows.${row}.us`)}
              </span>
            </div>
          ))}
        </div>

        <p className={styles.note}>{t('note')}</p>
      </div>
    </section>
  );
}
